/**
 *
 * DateTimePicker
 *
 */

import {Component} from "react";
import * as PropTypes from "prop-types";
import styled from "styled-components";
import DatePicker from "components/BookDayCalendar/entry";
import enhanceWithClickOutside from "react-click-outside";
import {isIE} from "react-device-detect";
import FormattedDate from "basicComponents/FormattedDate/Formatted";
import {FormattedMessage} from "react-intl";
import GlobalMessages from "messages/messages";
import messages from "./messages";
import TimePicker from "./TimePicker";
import {DATE_FORMATS, format} from "../../utils/Date";
import {hasRole, ROLES} from "../../utils/userStorage";
import ScannersAvailable from "./ScannersAvailable";

class DateTimePicker extends Component {
  static propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    hours: PropTypes.number,
    onHoursChange: PropTypes.func,
    minHours: PropTypes.number,
    maxHours: PropTypes.number,
    noHoursLoop: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    highlightedValue: PropTypes.array,
    alwaysOpen: PropTypes.bool,
    treatments: PropTypes.array.isRequired,
    scannersAvailable: PropTypes.number,
    scannersTotal: PropTypes.number,
  };

  static defaultProps = {
    hours: -1,
    highlightedValue: [],
  };

  state = {
    open: false,
  };

  handleClickOutside() {
    this.setState({
      open: false,
    });
  }

  handleOpen = () => {
    this.setState({
      open: true,
    });
  };

  handleDateChanged = date => {
    this.setState({ open: false });
    this.props.onChange(date);
  };

  handleHoursChanged = hours => {
    this.props.onHoursChange(hours);
  };

  shouldShowTimePicker() {
    return this.props.hours >= 0 && this.props.onHoursChange;
  }

  render() {
    const showTimePicker = this.shouldShowTimePicker();
    const previewTime = showTimePicker && this.props.hours !== -1;
    const isOpen = this.state.open || this.props.alwaysOpen;
    const isAbsolute = !this.props.alwaysOpen;

    return (
      <Wrapper showTimePicker={showTimePicker} id="newTreatmentDateTimePicker">
        <DateTextWrapper open={isOpen}>
          <DateText onClick={this.handleOpen}>
            {this.props.value ? (
              <FormattedDate
                date={this.props.value}
                format={DATE_FORMATS.HUMAN_READABLE_DATE}
              />
            ) : (
              <FormattedMessage {...messages.datePlaceholder} />
            )}
            {previewTime && (
              <>
                {` | ${this.props.hours} `}
                <FormattedMessage {...GlobalMessages.hoursFull} />
              </>
            )}
          </DateText>
        </DateTextWrapper>
        <Content absolute={isAbsolute} open={isOpen} id="dateTimePicker">
          <DatePickerWrapper>
            <DatePicker
              treatments={this.props.treatments}
              minDetail="month"
              highlightedValue={this.props.highlightedValue}
              minDate={this.props.minDate}
              onChange={this.handleDateChanged}
              value={this.props.value}
              formatShortWeekday={formatDayLabel}
            />
          </DatePickerWrapper>
          <SideWrapper>
          {showTimePicker && (
            <TimePicker
              min={this.props.minHours}
              max={this.props.maxHours}
              noLoop={this.props.noHoursLoop}
              hours={this.props.hours}
              onChange={this.handleHoursChanged}
            />
          )}
          { showTimePicker && hasRole(ROLES.Admin) && (
            <ScannersAvailable
              available={this.props.scannersAvailable}
              total={this.props.scannersTotal}
            />)}
          </SideWrapper>
        </Content>
      </Wrapper>
    );
  }
}

function formatDayLabel(locale, date) {
  return format("en", "dd", date);
}

export default enhanceWithClickOutside(DateTimePicker);

const DateTextWrapper = styled.div`
  background-color: ${({ theme, open }) =>
    open ? theme.borderColor : "transparent"};
  border-top-left-radius: ${({ theme }) => theme.borderRadius};
  border-top-right-radius: ${({ theme }) => theme.borderRadius};
  overflow: visible;
`;

const DateText = styled.div`
  width: inherit;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.borderColor};
  padding-left: ${({ theme }) => theme.padding}rem;
  padding-right: ${({ theme }) => theme.padding}rem;
  border-radius: ${({ theme }) => theme.borderRadius};
  background-color: white;
`;

const Content = styled.div`
  position: ${({ absolute }) => (absolute ? "absolute" : "inherit")};
  width: inherit;
  display: flex;
  flex-direction: row;
  height: ${({ open }) => (open ? "260px" : 0)};
  overflow: hidden;
  transition: height 150ms ease-out;
  align-items: flex-start;
`;

const DatePickerWrapper = styled.div`
  ${isIE ? "width: 250px" : "max-width: 250px;"};
`;

const Wrapper = styled.div`
  width: ${({ showTimePicker }) =>
    showTimePicker ? "calc(7rem + 250px)" : "250px"};
  position: relative;
  -webkit-border-radius: 10px;
  z-index: 99999;
`;

const SideWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
